import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Gleap from 'gleap';
import { SessionContext } from '@/auth/SessionContext';

const GLEAP_STYLE_ID = 'cakto-gleap-style-overrides';
const GLEAP_TOP_OFFSET = 92;
const GLEAP_BOTTOM_OFFSET = 16;

const GLEAP_WIDGET_SELECTOR =
  '.bb-feedback-button, .bb-feedback-dialog-container, [id*="gleap" i], [class*="gleap" i], iframe[src*="gleap" i]';

const GLEAP_DIALOG_SELECTOR =
  '.bb-feedback-dialog-container, [class*="gleap" i][class*="dialog" i], [class*="gleap" i][class*="modal" i]';

const GLEAP_BUTTON_SELECTOR =
  '.bb-feedback-button, [class*="gleap" i][class*="button" i], [class*="gleap" i][class*="launcher" i]';

const GLEAP_IFRAME_SELECTOR =
  'iframe[src*="gleap" i], iframe[id*="gleap" i], iframe[class*="gleap" i], .bb-feedback-button iframe';

// Com keepMounted (menu lateral do celular, varios drawers e menus) o modal fechado continua no
// DOM so com a classe MuiModal-hidden; contar so o .MuiModal-root sumiria com o lancador de vez.
const OPEN_MUI_MODAL_SELECTOR = '.MuiModal-root:not(.MuiModal-hidden)';
const MUI_MODAL_OPEN_BODY_CLASS = 'cakto-mui-modal-open';

const STUDENT_VIEW_PAGE_AS = 'student';

const getGleapApiKey = () => process.env.REACT_APP_GLEAP_API_KEY?.trim();

// O chat da Gleap e exclusivo do produtor logado no app: nao sobe no cadastro, na area do
// aluno/comprador nem para quem esta no ambiente de aluno. Comprador usa a central de ajuda.
export const isProducerSupportSession = (user) => {
  if (!user || user.registrationPending) {
    return false;
  }

  if (user.viewPageAs === STUDENT_VIEW_PAGE_AS) {
    return false;
  }

  return Boolean(user.is_producer);
};

const getUserName = (user) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');

  return fullName || user?.displayName || user?.name || user?.username || user?.email || null;
};

const getUserPhone = (user) => {
  if (!user?.cellphone) {
    return null;
  }

  const countryCode = user.phoneCountryCode ? `+${user.phoneCountryCode}` : '';

  return `${countryCode}${user.cellphone}`;
};

const getUserId = (user) => {
  if (!user?.id && !user?.email) {
    return null;
  }

  return String(user.id || user.email);
};

const getUserProfile = (user) => {
  const isProducer = Boolean(user?.is_producer);
  const isStudent = Boolean(user?.is_student);

  if (isProducer && isStudent) {
    return 'Produtor e comprador/aluno';
  }

  return isProducer ? 'Produtor' : 'Comprador/aluno';
};

const getCurrentEnvironment = (viewPageAs) => {
  if (viewPageAs === 'producer') {
    return 'Produtor';
  }

  if (viewPageAs === 'student') {
    return 'Comprador/aluno';
  }

  return null;
};

const getUserCustomData = (user) => {
  const currentEnvironment = getCurrentEnvironment(user?.viewPageAs);

  return {
    ...(user?.viewPageAs ? { viewPageAs: user.viewPageAs } : {}),
    isProducer: Boolean(user?.is_producer),
    isStudent: Boolean(user?.is_student),
    perfilCakto: getUserProfile(user),
    ...(currentEnvironment ? { ambienteAtual: currentEnvironment } : {}),
  };
};

const getGleapEnvironment = () => {
  if (process.env.NODE_ENV !== 'production') {
    return 'dev';
  }

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const isStaging = /stg|stag|hml|homolog/i.test(apiUrl);

  return isStaging ? 'staging' : 'prod';
};

const getHexColor = (color, fallback) => (typeof color === 'string' ? color : fallback);

const getGleapZIndex = (theme) => (Number(theme?.zIndex?.modal) || 1300) + 20;

const setImportantStyle = (element, property, value) => {
  if (!element?.style) {
    return;
  }

  element.style.setProperty(property, value, 'important');
};

const injectGleapStyleOverrides = (theme) => {
  const zIndex = getGleapZIndex(theme);
  const buttonZIndex = zIndex - 1;
  const maxHeightOffset = GLEAP_TOP_OFFSET + GLEAP_BOTTOM_OFFSET;

  let styleElement = document.getElementById(GLEAP_STYLE_ID);

  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = GLEAP_STYLE_ID;
    document.head.appendChild(styleElement);
  }

  styleElement.innerHTML = `
    .bb-feedback-dialog-container,
    [class*="gleap" i][class*="dialog" i],
    [class*="gleap" i][class*="modal" i] {
      z-index: ${zIndex} !important;
    }

    .bb-feedback-dialog-container {
      top: ${GLEAP_TOP_OFFSET}px !important;
      bottom: ${GLEAP_BOTTOM_OFFSET}px !important;
      height: calc(100dvh - ${maxHeightOffset}px) !important;
      max-height: calc(100dvh - ${maxHeightOffset}px) !important;
      overflow: hidden !important;
    }

    @supports not (height: 100dvh) {
      .bb-feedback-dialog-container {
        height: calc(100vh - ${maxHeightOffset}px) !important;
        max-height: calc(100vh - ${maxHeightOffset}px) !important;
      }
    }

    .bb-feedback-button,
    [class*="gleap" i][class*="button" i],
    [class*="gleap" i][class*="launcher" i] {
      z-index: ${buttonZIndex} !important;
      border: 0 !important;
      outline: 0 !important;
      box-shadow: none !important;
      filter: none !important;
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
      overflow: visible !important;
    }

    /* O lancador fica acima dos modais do MUI (1300) e, no celular, cobre a acao do canto
       inferior direito deles. Com o chat aberto (--open) ele e o botao de fechar e fica.
       A pilha de notificacoes (previa de mensagem, news) e irma do lancador no body, no mesmo
       canto e com o mesmo z-index, e cobre o botao do modal do mesmo jeito. */
    body.${MUI_MODAL_OPEN_BODY_CLASS} .bb-feedback-button:not(.bb-feedback-button--open),
    body.${MUI_MODAL_OPEN_BODY_CLASS} .gleap-notification-container {
      display: none !important;
    }

    .bb-feedback-button *,
    [class*="gleap" i][class*="button" i] *,
    [class*="gleap" i][class*="launcher" i] * {
      border: 0 !important;
      outline: 0 !important;
      box-shadow: none !important;
      filter: none !important;
      background-image: none !important;
    }

    .bb-feedback-button::before,
    .bb-feedback-button::after,
    .bb-feedback-button *::before,
    .bb-feedback-button *::after,
    [class*="gleap" i][class*="button" i]::before,
    [class*="gleap" i][class*="button" i]::after,
    [class*="gleap" i][class*="button" i] *::before,
    [class*="gleap" i][class*="button" i] *::after,
    [class*="gleap" i][class*="launcher" i]::before,
    [class*="gleap" i][class*="launcher" i]::after,
    [class*="gleap" i][class*="launcher" i] *::before,
    [class*="gleap" i][class*="launcher" i] *::after {
      border: 0 !important;
      box-shadow: none !important;
      filter: none !important;
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
    }

    .bb-feedback-button iframe,
    iframe[src*="gleap" i] {
      border: 0 !important;
      outline: 0 !important;
      box-shadow: none !important;
      filter: none !important;
      background: transparent !important;
      background-color: transparent !important;
      background-image: none !important;
    }
  `;
};

const removeGleapStyleOverrides = () => {
  const styleElement = document.getElementById(GLEAP_STYLE_ID);

  if (styleElement) {
    styleElement.remove();
  }

  document.body.classList.remove(MUI_MODAL_OPEN_BODY_CLASS);
};

const syncGleapLauncherWithMuiModals = () => {
  const hasOpenModal = Boolean(document.querySelector(OPEN_MUI_MODAL_SELECTOR));

  document.body.classList.toggle(MUI_MODAL_OPEN_BODY_CLASS, hasOpenModal);
};

// Abrir ou fechar um modal keepMounted so troca a classe do .MuiModal-root, sem no novo.
const isMuiModalClassChange = (mutation) =>
  mutation.type === 'attributes' && mutation.target.classList?.contains('MuiModal-root');

const adjustGleapLayout = (theme) => {
  const zIndex = String(getGleapZIndex(theme));
  const buttonZIndex = String(getGleapZIndex(theme) - 1);
  const maxHeight = `calc(100dvh - ${GLEAP_TOP_OFFSET + GLEAP_BOTTOM_OFFSET}px)`;

  try {
    document.querySelectorAll(GLEAP_WIDGET_SELECTOR).forEach((element) => {
      setImportantStyle(element, 'z-index', zIndex);
    });

    document.querySelectorAll(GLEAP_DIALOG_SELECTOR).forEach((element) => {
      setImportantStyle(element, 'z-index', zIndex);
      setImportantStyle(element, 'top', `${GLEAP_TOP_OFFSET}px`);
      setImportantStyle(element, 'bottom', `${GLEAP_BOTTOM_OFFSET}px`);
      setImportantStyle(element, 'height', maxHeight);
      setImportantStyle(element, 'max-height', maxHeight);
      setImportantStyle(element, 'overflow', 'hidden');
    });

    document.querySelectorAll(GLEAP_BUTTON_SELECTOR).forEach((element) => {
      setImportantStyle(element, 'z-index', buttonZIndex);
      setImportantStyle(element, 'border', '0');
      setImportantStyle(element, 'outline', '0');
      setImportantStyle(element, 'box-shadow', 'none');
      setImportantStyle(element, 'filter', 'none');
      setImportantStyle(element, 'background', 'transparent');
      setImportantStyle(element, 'background-color', 'transparent');
      setImportantStyle(element, 'background-image', 'none');
      setImportantStyle(element, 'overflow', 'visible');

      element.querySelectorAll('*').forEach((child) => {
        setImportantStyle(child, 'border', '0');
        setImportantStyle(child, 'outline', '0');
        setImportantStyle(child, 'box-shadow', 'none');
        setImportantStyle(child, 'filter', 'none');
        setImportantStyle(child, 'background-image', 'none');
      });
    });

    document.querySelectorAll(GLEAP_IFRAME_SELECTOR).forEach((element) => {
      setImportantStyle(element, 'border', '0');
      setImportantStyle(element, 'outline', '0');
      setImportantStyle(element, 'box-shadow', 'none');
      setImportantStyle(element, 'filter', 'none');
      setImportantStyle(element, 'background', 'transparent');
      setImportantStyle(element, 'background-color', 'transparent');
      setImportantStyle(element, 'background-image', 'none');
    });
  } catch (error) {
    // Ignore selector support issues in older embedded browsers.
  }
};

const softReInitializeGleap = () => {
  const gleapInstance = Gleap.getInstance?.();

  if (gleapInstance?.softReInitialize) {
    gleapInstance.softReInitialize();
  }
};

const configureGleap = (theme) => {
  Gleap.disableConsoleLogOverwrite();
  Gleap.setUseCookies(true);
  Gleap.setEnvironment(getGleapEnvironment());

  const primaryColor = getHexColor(theme.palette.primary.main, '#0f7865');

  try {
    Gleap.setStyles({
      primaryColor,
      headerColor: primaryColor,
      buttonColor: primaryColor,
      backgroundColor: getHexColor(theme.palette.background.paper, '#ffffff'),
    });
  } catch (error) {
    // Keep the support widget available even if Gleap rejects theme overrides.
  }
};

const initializeGleap = (apiKey, theme) => {
  try {
    configureGleap(theme);

    if (window.__caktoGleapInitialized) {
      softReInitializeGleap();
      Gleap.showFeedbackButton(true);
      return;
    }

    Gleap.initialize(apiKey);
    Gleap.showFeedbackButton(true);
    window.__caktoGleapInitialized = true;
  } catch (error) {
    window.__caktoGleapInitialized = false;
  }
};

const setGleapFeedbackButtonVisibility = (showButton) => {
  try {
    Gleap.showFeedbackButton(Boolean(showButton));
  } catch (error) {
    // The SDK may not be initialized yet.
  }
};

// Abre o chat do produtor. Devolve false quando a Gleap nao esta disponivel na sessao
// (nao-produtor, ambiente de aluno, chave ausente) pra quem chama cair na central de ajuda.
export const openSupportChat = () => {
  try {
    if (!window.__caktoGleapInitialized || typeof Gleap?.open !== 'function') {
      return false;
    }

    Gleap.open();

    return true;
  } catch (error) {
    return false;
  }
};

// Diz se o painel do chat esta aberto de verdade. O open() e aceito mesmo quando
// o widget nao aparece, e nesse caso quem chamou precisa de uma saida.
export const isSupportChatOpen = () => {
  try {
    return typeof Gleap?.isOpened === 'function' && Boolean(Gleap.isOpened());
  } catch (error) {
    return false;
  }
};

const disableGleapChat = () => {
  try {
    Gleap.destroy();
  } catch (error) {
    // The SDK may not be initialized yet.
  }

  removeGleapStyleOverrides();
  window.__caktoGleapInitialized = false;
  window.__caktoGleapIdentityId = null;
  window.__caktoGleapMountCount = 0;
};

const identifyGleapUser = (user) => {
  try {
    if (!window.__caktoGleapInitialized) {
      return;
    }

    const userId = getUserId(user);

    if (!userId) {
      if (window.__caktoGleapIdentityId) {
        Gleap.clearIdentity();
        window.__caktoGleapIdentityId = null;
      }

      return;
    }

    const customerData = {
      name: getUserName(user),
      email: user.email || null,
      phone: getUserPhone(user),
      avatar: user.picture || user.photoURL || null,
      customData: getUserCustomData(user),
    };

    if (window.__caktoGleapIdentityId === userId) {
      Gleap.updateContact(customerData);
      return;
    }

    Gleap.identify(userId, customerData);
    window.__caktoGleapIdentityId = userId;
  } catch (error) {
    window.__caktoGleapIdentityId = null;
  }
};

const GleapChat = ({ showButton = true }) => {
  const theme = useTheme();
  const sessionContext = useContext(SessionContext);
  const user = sessionContext?.user;
  const isProducerSession = isProducerSupportSession(user);

  useEffect(() => {
    const gleapApiKey = getGleapApiKey();

    if (!gleapApiKey || !isProducerSession) {
      disableGleapChat();
      return undefined;
    }

    window.__caktoGleapMountCount = (window.__caktoGleapMountCount || 0) + 1;

    initializeGleap(gleapApiKey, theme);
    setGleapFeedbackButtonVisibility(showButton);

    if (!window.__caktoGleapInitialized) {
      return undefined;
    }

    injectGleapStyleOverrides(theme);
    adjustGleapLayout(theme);
    syncGleapLauncherWithMuiModals();

    let modalSyncFrame = null;

    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length > 0)) {
        requestAnimationFrame(() => {
          adjustGleapLayout(theme);
        });
      }

      const mayHaveChangedModals = mutations.some(
        (mutation) => mutation.type === 'childList' || isMuiModalClassChange(mutation)
      );

      if (mayHaveChangedModals && modalSyncFrame === null) {
        modalSyncFrame = requestAnimationFrame(() => {
          modalSyncFrame = null;
          syncGleapLauncherWithMuiModals();
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observer.disconnect();
      cancelAnimationFrame(modalSyncFrame);
      window.__caktoGleapMountCount = Math.max((window.__caktoGleapMountCount || 1) - 1, 0);

      if (window.__caktoGleapMountCount === 0) {
        Gleap.destroy();
        removeGleapStyleOverrides();
        window.__caktoGleapInitialized = false;
        window.__caktoGleapIdentityId = null;
      }
    };
  }, [showButton, theme, isProducerSession]);

  useEffect(() => {
    if (!isProducerSession) {
      return;
    }

    identifyGleapUser(user);
  }, [user, isProducerSession]);

  return null;
};

GleapChat.propTypes = {
  showButton: PropTypes.bool,
};

export default GleapChat;
